import { Component, EventEmitter, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DatePipe, JsonPipe, NgForOf, NgIf } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ClrDatepickerModule, ClrInputModule, ClrRadioModule, ClrSelectModule, ClrStepperModule } from "@clr/angular";
import { ClarityIcons, twoWayArrowsIcon } from "@cds/core/icon";
import { Subject } from "rxjs";
import { Conditions, ConfirmationSteps, Confirmations, Personen, Staff, WebserviceResponse } from "../../../_shared/interfaces";
import {
  AlertService,
  ConditionStoreService,
  ConfirmationStoreService,
  IAlertType,
  PersonsStoreService,
  StaffStoreService
} from "../../../_shared/services";

import { environment as env } from "../../../../environments/environment";
import {TranslocoDirective} from "@jsverse/transloco";

import { CountrySelectComponent } from '../../../_shared/components/country-select/country-select.component';
import { sichtbareMitarbeiter } from "../../../_shared/utils/staff-selection";

@Component({
  selector: 'app-confirmation-form',
  standalone: true,
  imports: [
    CountrySelectComponent,
    ClrStepperModule,
    ReactiveFormsModule,
    ClrInputModule,
    ClrSelectModule,
    ClrDatepickerModule,
    NgIf,
    DatePipe,
    JsonPipe,
    RouterLink,
    ClrRadioModule,
    NgForOf,
    TranslocoDirective
  ],
  templateUrl: './confirmation-form.component.html',
  styleUrl: './confirmation-form.component.sass',
  providers: [{ provide: LOCALE_ID, useValue: 'de' }]
})
export class ConfirmationFormComponent implements OnInit, OnChanges {
  confirmationForm!: FormGroup;
  private defaultDate: Date = new Date();
  persons: Personen[] = [];
  /** Ungefilterte Antwort des Servers. Fuer die Anzeige siehe `staff`. */
  staffAll: Staff[] = [];

  /**
   * Nur aktive Mitarbeiter -- und zusaetzlich der aktuell zugewiesene, auch
   * wenn er passiv ist. Siehe `sichtbareMitarbeiter`; ohne diese Ausnahme
   * verloere das Bearbeiten eines Altvorgangs seinen Bearbeiter.
   */
  get staff(): Staff[] {
    return sichtbareMitarbeiter(this.staffAll, this.confirmationForm);
  }
  delivery_conditions: Conditions[] = [];
  payment_conditions: Conditions[] = [];
  offer_conditions: Conditions[] = [];

  @Input() projectId: string = '';
  @Input() jobId: number = 0;
  @Input() confirmationId: number = 0;
  @Input() confirmationData?: Confirmations;
  @Input() isUpdateMode = true;
  @Output() formSubmit = new EventEmitter<ConfirmationSteps>();

  devMode: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private confss: ConfirmationStoreService,
    private personsService: PersonsStoreService,
    private staffService: StaffStoreService,
    private condss: ConditionStoreService,
    private alertDispatcher: AlertService,
    private router: Router,
    private fb: FormBuilder,
    private datePipe: DatePipe,
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon);
    this.devMode = env.stages.dev;
    this.initForm();
  }

  ngOnInit() {
    this.loadStaff();
    this.loadPayConds();
    this.loadDeliveryConds();
    this.loadOfferConds();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['confirmationData'] && this.confirmationData) {
      this.patchFormValues(this.confirmationData);
    }
    const companyId = parseInt(this.splitProjectNumber(this.projectId, 0), 10);
    if (!isNaN(companyId)) {
      this.loadPersons(companyId);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    this.confirmationForm = this.fb.group({
      stepI: this.fb.group({
        project_id: [''],
        confirmation_id: [''],
        company_name: [''],
        street: [''],
        postcode: [''],
        city: [''],
        country: [''],
        status: ['open'],
        name_project: [''],
        description: [''],
        request_id: [''],
        order_id: [''],
        job_date: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        delivery_date: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
      }),
      stepII: this.fb.group({
        employee_phm: this.fb.control<number | null>(null),
      }),
      stepIII: this.fb.group({
        employee_leader: this.fb.control<number | null>(null),
      }),
      stepIV: this.fb.group({
        cond_offer: this.fb.control<number | null>(null),
        cond_payment: this.fb.control<number | null>(null),
        cond_delivery: this.fb.control<number | null>(null),
        delivery_remarks: [''],
      }),
      stepV: this.fb.group({
        delivery_company: [''],
        delivery_department: [''],
        delivery_street: [''],
        delivery_city: [''],
        delivery_postcode: [''],
        delivery_country: [''],
      })
    })
  }

  private patchFormValues(data: Confirmations) {
    if (!data) return;

    const formValue = {
      stepI: {
        project_id: data.project_id || this.projectId,
        confirmation_id: data.confirmation_id || this.confirmationId,
        company_name: data.company_name || '',
        street: data.street || '',
        postcode: data.postcode || '',
        city: data.city || '',
        country: this.mapCountryForForm(data.country || ''),
        status: data.status || 'open',
        name_project: data.name_project || '',
        description: data.description || '',
        request_id: data.request_id || '',
        order_id: data.order_id || '',
        job_date: this.datePipe.transform(data.job_date, 'dd.MM.yyyy'),
        delivery_date: this.datePipe.transform(data.delivery_date, 'dd.MM.yyyy'),
      },
      stepII: {
        employee_phm: data.employee_phm || 0,
      },
      stepIII: {
        employee_leader: data.employee_leader || 0,
      },
      stepIV: {
        cond_offer: data.cond_offer || 0,
        cond_payment: data.cond_payment || 0,
        cond_delivery: data.cond_delivery || 0,
        delivery_remarks: data.delivery_remarks || '',
      },
      stepV: {
        delivery_company: data.delivery_company || '',
        delivery_department: data.delivery_department || '',
        delivery_street: data.delivery_street || '',
        delivery_city: data.delivery_city || '',
        delivery_postcode: data.delivery_postcode || '',
        delivery_country: this.mapCountryForForm(data.delivery_country || ''),
      }
    };
    this.confirmationForm.patchValue(formValue);
  }

  private mapCountryForForm(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  splitProjectNumber(projectId: string, substring: number) {
    const splits = projectId.split('.');
    return splits[substring];
  }

  projectLeader(): number {
    return this.confirmationForm.get('stepIII')?.get('employee_leader')?.value || 0;
  }

  projectStaff(): number {
    return this.confirmationForm.get('stepII')?.get('employee_phm')?.value || 0;
  }

  get selectedDeliveryHasOption(): boolean {
    const condId = Number(this.confirmationForm.value?.stepIV?.cond_delivery);
    if (!condId) return false;
    const cond = this.delivery_conditions.find(c => c.cond_id === condId);
    return cond?.option ?? false;
  }

  updateConfirmation() {
    if (this.confirmationForm.valid) {
      const formValue = this.confirmationForm.value;
      this.confss.updateConfirmation(formValue, this.jobId).subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: 'Auftragsbestätigung erfolgreich aktualisiert',
            clrAlertType: IAlertType.SUCCESS
          }, 5000);
          this.formSubmit.emit(formValue);
          this.router.navigate(['/confirmation/details', this.projectId, this.jobId, this.confirmationId]);
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren der Auftragsbestätigung: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
        }
      });
    }
  }

  private loadStaff() {
    this.staffService.getStaff().subscribe({
      next: (response: WebserviceResponse<Staff[]>) => {
        if (response?.object) {
          this.staffAll = response.object;
        }
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Mitarbeiter: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  private loadPersons(companyId: number) {
    this.personsService.getPersonInCompany(companyId, true).subscribe({
      next: (response: WebserviceResponse<Personen[]>) => {
        if (response?.object) {
          this.persons = response.object;
        }
      },
      complete: () => {
        this.persons.sort((a, b) => a.lastname.localeCompare(b.lastname));
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Ansprechpartner: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  private loadOfferConds() {
    this.condss.getConditionByType(3).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.offer_conditions = data.object;
        }
      }
    });
  }

  private loadPayConds() {
    this.condss.getConditionByType(2).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.payment_conditions = data.object;
        }
      }
    });
  }

  private loadDeliveryConds() {
    this.condss.getConditionByType(1).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.delivery_conditions = data.object;
        }
      }
    });
  }

}
